import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import { Bell, ExternalLink, Calendar, Users, ArrowLeft, ChevronRight, GraduationCap, IndianRupee, ListChecks, FileText, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = "https://vedantiacademy.com";

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const Block = ({ icon: Icon, title, value }) => {
  if (!value) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-5">
      <h2 className="font-heading font-bold text-vedanti-navy flex items-center gap-2 mb-2">
        <Icon size={18} className="text-vedanti-orange" /> {title}
      </h2>
      <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{value}</p>
    </div>
  );
};

export default function NotificationDetailPage() {
  const { id } = useParams();
  const [n, setN] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let active = true;
    setStatus("loading");
    fetch(`${API}/api/notifications/${id}`)
      .then((r) => { if (!r.ok) throw new Error("not found"); return r.json(); })
      .then((d) => { if (active) { setN(d); setStatus("ok"); } })
      .catch(() => { if (active) setStatus("error"); });
    return () => { active = false; };
  }, [id]);

  const detailsHtml = useMemo(
    () => (n?.details ? DOMPurify.sanitize(n.details, { USE_PROFILES: { html: true } }) : ""),
    [n]
  );

  if (status === "loading")
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 border-4 border-vedanti-orange border-t-transparent rounded-full animate-spin" /></div>;

  if (status === "error")
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="notif-detail-notfound">
        <h1 className="text-2xl font-bold text-vedanti-navy">Notification Not Found</h1>
        <Link to="/notifications" className="text-vedanti-orange font-semibold hover:underline flex items-center gap-1"><ArrowLeft size={16} /> Back to Notifications</Link>
      </div>
    );

  const jobLd = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: n.title,
    description: n.summary || n.details || n.title,
    datePosted: n.posted_date,
    validThrough: n.last_date,
    employmentType: "FULL_TIME",
    hiringOrganization: { "@type": "Organization", name: n.exam_name },
    jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressCountry: "IN" } },
  };

  const facts = [
    { icon: Users, label: "Vacancies", value: n.vacancies },
    { icon: Calendar, label: "Last Date", value: fmtDate(n.last_date) },
    { icon: Calendar, label: "Exam Date", value: fmtDate(n.exam_date) || n.exam_date },
    { icon: GraduationCap, label: "Eligibility", value: n.eligibility_short },
    { icon: IndianRupee, label: "Salary", value: n.salary },
    { icon: IndianRupee, label: "Application Fee", value: n.application_fee },
  ].filter((f) => f.value);

  return (
    <div data-testid="notification-detail-page">
      <Helmet>
        <title>{`${n.title} — Eligibility, Vacancies & Apply | Vedanti Academy`}</title>
        <meta name="description" content={n.summary || `${n.exam_name} notification details — vacancies, last date, eligibility and how to apply.`} />
        <link rel="canonical" href={`${SITE}/notifications/${n.slug || n.id}`} />
        <meta property="og:title" content={n.title} />
        <meta property="og:description" content={n.summary || n.exam_name} />
        <script type="application/ld+json">{JSON.stringify(jobLd)}</script>
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="container-main py-3 flex items-center gap-2 text-sm text-slate-500">
          <Link to="/" className="hover:text-vedanti-orange">Home</Link>
          <ChevronRight size={14} />
          <Link to="/notifications" className="hover:text-vedanti-orange">Notifications</Link>
          <ChevronRight size={14} />
          <span className="text-vedanti-navy font-medium truncate max-w-[220px]">{n.exam_name}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden bg-vedanti-navy">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.2)_0%,_transparent_55%)]" />
        <div className="absolute bottom-0 inset-x-0 h-1.5 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange" />
        <div className="container-main py-10 md:py-14 relative z-10">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-vedanti-orange/20 text-vedanti-mango">
            <Bell size={12} /> {n.category} · {n.exam_name}
          </span>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight mt-3">{n.title}</h1>
          {n.summary && <p className="text-slate-300 mt-3 max-w-3xl text-base">{n.summary}</p>}
          <div className="mt-6 flex flex-wrap gap-3">
            {n.apply_url && (
              <a href={n.apply_url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-2 bg-vedanti-orange hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-lg" data-testid="notif-detail-apply">
                Apply on Official Site <ExternalLink size={14} />
              </a>
            )}
            {n.course_slug && (
              <Link to={`/courses/${n.course_slug}`} className="inline-flex items-center gap-2 border-2 border-white/30 text-white hover:bg-white/10 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all">
                <GraduationCap size={15} /> Coaching for this Exam
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="section-padding bg-gradient-to-b from-white to-slate-50">
        <div className="container-main max-w-4xl space-y-6">
          {/* Quick facts */}
          {facts.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="notif-detail-facts">
              {facts.map((f) => (
                <div key={f.label} className="bg-white rounded-xl border border-slate-100 p-4">
                  <p className="text-[11px] uppercase font-semibold text-slate-500 tracking-wider flex items-center gap-1"><f.icon size={12} /> {f.label}</p>
                  <p className="font-bold text-vedanti-navy mt-1">{f.value}</p>
                </div>
              ))}
            </div>
          )}

          {detailsHtml && (
            <div className="bg-white rounded-xl border border-slate-100 p-5 md:p-7">
              <div className="prose prose-slate max-w-none prose-headings:font-heading prose-headings:text-vedanti-navy prose-h2:text-lg prose-h3:text-base prose-p:text-slate-600 prose-li:text-slate-600 prose-strong:text-vedanti-navy" dangerouslySetInnerHTML={{ __html: detailsHtml }} data-testid="notif-detail-body" />
            </div>
          )}

          <Block icon={GraduationCap} title="Eligibility" value={n.eligibility_full} />
          <Block icon={ListChecks} title="Selection Process" value={n.selection_process} />
          <Block icon={FileText} title="How to Apply" value={n.how_to_apply} />

          {!detailsHtml && !n.eligibility_full && !n.selection_process && !n.how_to_apply && (
            <div className="bg-amber-50 border border-amber-100 text-amber-800 rounded-xl p-4 flex items-center gap-2 text-sm" data-testid="notif-detail-minimal">
              <AlertCircle size={16} /> Detailed information will be updated soon. Use the official apply link above for the full notification.
            </div>
          )}

          {/* CTA */}
          <div className="bg-gradient-to-r from-vedanti-navy to-blue-900 rounded-2xl p-8 text-center">
            <h3 className="font-heading text-xl font-bold text-white mb-2">Preparing for {n.exam_name}?</h3>
            <p className="text-slate-300 text-sm mb-5">Get a free demo class and expert guidance from Vedanti Academy.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/demo-class" className="bg-vedanti-orange hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition-colors" data-testid="notif-detail-cta-demo">Book Free Demo</Link>
              <Link to="/consultancy" className="border border-white/25 text-white hover:bg-white/10 font-semibold px-6 py-3 rounded-xl transition-colors">Get Counselling</Link>
            </div>
          </div>

          <Link to="/notifications" className="inline-flex items-center gap-2 text-vedanti-orange font-semibold hover:gap-3 transition-all" data-testid="notif-detail-back">
            <ArrowLeft size={16} /> Back to All Notifications
          </Link>
        </div>
      </section>
    </div>
  );
}
